import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faToggleOn, faToggleOff } from '@fortawesome/free-solid-svg-icons';
import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants';
import { Button } from '@/components/ui';
import { useModalStore } from '@/stores';
import styles from './ProviderModals.module.css';

interface Provider {
  id: string;
  nombre_comercial: string;
  state: string;
}

interface ToggleProviderStateModalProps {
  provider: Provider;
  onSuccess?: () => void;
}

export function ToggleProviderStateModal({ provider, onSuccess }: ToggleProviderStateModalProps) {
  const { closeModal } = useModalStore();
  const queryClient = useQueryClient();

  const isActive = provider.state === 'activo';
  const newState = isActive ? 'inactivo' : 'activo';

  const mutation = useMutation({
    mutationFn: async () => {
      await api.patch(`${API_ROUTES.providers}${provider.id}/`, { state: newState });
    },
    onSuccess: () => {
      toast.success(`Provider ${isActive ? 'deactivated' : 'activated'} successfully`);
      queryClient.invalidateQueries({ queryKey: ['providers'] });
      queryClient.invalidateQueries({ queryKey: ['provider-stats'] });
      onSuccess?.();
      closeModal();
    },
    onError: (error: unknown) => {
      const apiError = error as { response?: { data?: { error?: string } } };
      const errorMessage =
        apiError.response?.data?.error ||
        `Error ${isActive ? 'deactivating' : 'activating'} provider`;
      toast.error(errorMessage);
    },
  });

  return (
    <div className={styles.deleteContainer}>
      <div
        className={`${styles.deleteIconContainer} ${isActive ? styles.toggleDeactivateIcon : styles.toggleActivateIcon}`}
      >
        <FontAwesomeIcon icon={isActive ? faToggleOff : faToggleOn} className={styles.deleteIcon} />
      </div>

      <h3 className={styles.deleteTitle}>{isActive ? 'Deactivate' : 'Activate'} Provider</h3>

      <p className={styles.deleteMessage}>
        Are you sure you want to {isActive ? 'deactivate' : 'activate'} the provider{' '}
        <strong>{provider.nombre_comercial}</strong>?
      </p>

      <p className={styles.deleteSubtext}>
        {isActive
          ? 'The provider will no longer appear in search results or chat recommendations.'
          : 'The provider will become visible in search results and chat recommendations.'}
      </p>

      <div className={styles.deleteActions}>
        <Button variant="secondary" onClick={closeModal} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button
          variant={isActive ? 'warning' : 'success'}
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          {mutation.isPending
            ? isActive
              ? 'Deactivating...'
              : 'Activating...'
            : isActive
              ? 'Deactivate'
              : 'Activate'}
        </Button>
      </div>
    </div>
  );
}
